import type { ProductVariantDto, PricelistItemDto } from "../../../api";

const sortTiersByMinQuantity = (
  tiers: PricelistItemDto[] | undefined | null
): PricelistItemDto[] => {
  if (!tiers || tiers.length === 0) {
    return [];
  }

  return [ ...tiers ].sort(
    (a, b) => (a.minQuantity ?? 0) - (b.minQuantity ?? 0)
  );
};

const findTierForQuantity = (
  tiers: PricelistItemDto[],
  quantity: number
): PricelistItemDto | null => {
  if (tiers.length === 0) {
    return null;
  }

  const safeQuantity = quantity > 0 ? quantity : 1;

  let matchedTier: PricelistItemDto | null = tiers[ 0 ];
  for (const tier of tiers) {
    const minQty = tier.minQuantity ?? 1;
    if (safeQuantity >= minQty) {
      matchedTier = tier;
    } else {
      break;
    }
  }

  return matchedTier;
};

/**
 * Get the unit price for a product variant based on the applicable pricelist tier.
 * Falls back to listPrice + priceExtra when no tiers are available.
 */
export function getProductUnitPrice(
  product: ProductVariantDto,
  quantity = 1
): number {
  const sortedTiers = sortTiersByMinQuantity(product.pricelistItems);
  const applicableTier = findTierForQuantity(sortedTiers, quantity);

  if (
    applicableTier &&
    typeof applicableTier.fixedPrice === "number" &&
    !Number.isNaN(applicableTier.fixedPrice)
  ) {
    return applicableTier.fixedPrice;
  }

  const listPrice = product.listPrice ?? 0;
  const priceExtra = product.priceExtra ?? 0;
  return listPrice + priceExtra;
}

/**
 * Convenience helper to get the total price for a cart line.
 */
export function getProductLineTotal(
  product: ProductVariantDto,
  quantity = 1
): number {
  const safeQuantity = quantity > 0 ? quantity : 0;
  return getProductUnitPrice(product, safeQuantity) * safeQuantity;
}


